import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCompany } from '@/context/CompanyContext';
import { AddCompanyModal } from '@/components/modals/AddCompanyModal';
import { Building2, Loader as Loader2, Plus, CircleCheck as CheckCircle2, Globe, Hash, Info, ChartLine as LineChart, Package, Users, ArrowLeft, Settings } from 'lucide-react';

const COUNTRY_NAMES: Record<string, string> = {
  IL: 'ישראל', US: 'United States', GB: 'United Kingdom', CA: 'Canada',
  AU: 'Australia', DE: 'Germany', FR: 'France', ES: 'Spain', IT: 'Italy',
  NL: 'Netherlands', SE: 'Sweden', NO: 'Norway', DK: 'Denmark', FI: 'Finland',
  PL: 'Poland', CH: 'Switzerland', AT: 'Austria', BE: 'Belgium', IE: 'Ireland',
  NZ: 'New Zealand', SG: 'Singapore', HK: 'Hong Kong', JP: 'Japan',
  KR: 'South Korea', IN: 'India', AE: 'United Arab Emirates', SA: 'Saudi Arabia',
  ZA: 'South Africa', BR: 'Brazil', MX: 'Mexico',
};

const MODULE_TAGS = [
  { key: 'cashflow',  label: 'תזרים מזומנים', icon: <LineChart className="w-3.5 h-3.5" />, color: 'bg-blue-50 text-blue-700 border-blue-100' },
  { key: 'inventory', label: 'ניהול מלאי',     icon: <Package  className="w-3.5 h-3.5" />, color: 'bg-emerald-50 text-emerald-700 border-emerald-100' },
  { key: 'hr',        label: 'עובדים',          icon: <Users    className="w-3.5 h-3.5" />, color: 'bg-amber-50 text-amber-700 border-amber-100' },
] as const;

const MODULE_ENTRY: Record<string, string> = {
  cashflow:  '/app/cashflow',
  inventory: '/app/inventory',
  hr:        '/app/hr',
};

export function WorkspacesPage() {
  const navigate = useNavigate();
  const { companies, loadingCompanies, setActiveCompanyId, refreshCompanies } = useCompany();
  const [showAddModal, setShowAddModal] = useState(false);

  if (loadingCompanies) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-primary-600 animate-spin" />
      </div>
    );
  }

  if (companies.length === 0) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-6 text-center px-4">
        <div className="w-20 h-20 bg-primary-50 rounded-2xl flex items-center justify-center">
          <Building2 className="w-10 h-10 text-primary-400" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-1">אין לך עדיין חברה</h2>
          <p className="text-gray-500 text-sm max-w-xs mx-auto">צור את החברה הראשונה שלך כדי להתחיל לעבוד</p>
        </div>
        <button
          onClick={() => navigate('/onboarding')}
          className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white text-sm font-medium rounded-xl hover:bg-primary-700 transition-colors shadow-sm"
        >
          <Plus className="w-4 h-4" />
          צור חברה ראשונה
        </button>
      </div>
    );
  }

  const handleCompanyCreated = async (newId: string) => {
    setShowAddModal(false);
    await refreshCompanies();
    setActiveCompanyId(newId);
  };

  const enterCompany = (companyId: string) => {
    setActiveCompanyId(companyId);
    const company = companies.find(c => c.id === companyId);
    const modules: string[] = (company as any)?.active_modules ?? [];
    const active = MODULE_TAGS.filter(m => modules.includes(m.key));
    if (active.length === 1 && active[0]) {
      navigate(MODULE_ENTRY[active[0].key] ?? '/app');
    } else {
      // Multiple modules or none — go to /app so sidebar shows picker
      navigate('/app');
    }
  };

  return (
    <>
      <div dir="rtl" className="min-h-[60vh] flex flex-col items-center justify-center py-10 px-4">
        <div className="w-full max-w-xl space-y-5">

          {/* Header */}
          <div className="text-center mb-2">
            <h1 className="text-2xl font-bold text-gray-900">ברוך הבא</h1>
            <p className="text-sm text-gray-500 mt-1">בחר את החברה שאליה ברצונך להיכנס</p>
          </div>

          {/* Company list */}
          {companies.map((company) => {
            const companyModules: string[] = (company as any)?.active_modules ?? [];
            const subscribedModules = MODULE_TAGS.filter(m => companyModules.includes(m.key));

            const statusLabel = company.status === 'verified'   ? { text: 'מאומת',         cls: 'bg-green-100 text-green-700' }
                              : company.status === 'suspended'   ? { text: 'מושהה',          cls: 'bg-red-100 text-red-700' }
                              : company.status === 'unverified'  ? { text: 'ממתין לאימות',  cls: 'bg-yellow-100 text-yellow-700' }
                              : null;

            const isAdmin = company.role === 'admin';

            return (
              <div
                key={company.id}
                className="relative bg-white border border-gray-200 rounded-2xl
                  shadow-sm hover:border-primary-400 hover:shadow-md
                  transition-all duration-150 group overflow-hidden"
              >
                {/* Clickable entry area */}
                <button
                  type="button"
                  onClick={() => enterCompany(company.id)}
                  className="w-full text-right px-5 py-4 flex items-center gap-4 hover:-translate-y-0.5 active:scale-[0.99] transition-transform duration-150"
                >

                  {/* Avatar */}
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200
                    group-hover:from-primary-500 group-hover:to-primary-700
                    flex items-center justify-center flex-shrink-0 transition-all duration-200 shadow-sm">
                    <Building2 className="w-6 h-6 text-gray-500 group-hover:text-white transition-colors duration-200" />
                  </div>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-base font-semibold text-gray-900 group-hover:text-primary-700 transition-colors">
                        {company.legal_name}
                      </span>
                      {statusLabel && (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full ${statusLabel.cls}`}>
                          {company.status === 'verified' && <CheckCircle2 className="w-3 h-3" />}
                          {company.status === 'unverified' && <Info className="w-3 h-3" />}
                          {statusLabel.text}
                        </span>
                      )}
                      {company.role && (
                        <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-600 rounded">
                          {company.role === 'admin' ? 'מנהל' : 'חבר'}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      <span className="flex items-center gap-1 text-xs text-gray-400">
                        <Globe className="w-3 h-3" />
                        {COUNTRY_NAMES[company.country] ?? company.country}
                      </span>
                      {company.tax_id && (
                        <span className="flex items-center gap-1 text-xs text-gray-400">
                          <Hash className="w-3 h-3" />
                          {company.tax_id}
                        </span>
                      )}
                    </div>

                    {/* Module tags */}
                    <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                      {subscribedModules.length > 0 ? (
                        subscribedModules.map(m => (
                          <span key={m.key}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full border ${m.color}`}>
                            {m.icon}
                            {m.label}
                          </span>
                        ))
                      ) : (
                        <span className="text-[11px] text-gray-400 italic">אין מודולים פעילים</span>
                      )}
                    </div>
                  </div>

                  {/* Arrow */}
                  <ArrowLeft className="w-5 h-5 text-gray-300 group-hover:text-primary-500 flex-shrink-0 transition-colors duration-200" />
                </button>

                {/* Company settings button — admin only, outside the entry button */}
                {isAdmin && (
                  <div className="px-5 pb-3 flex justify-end">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveCompanyId(company.id);
                        navigate('/app/settings/company');
                      }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium
                        text-gray-500 bg-gray-50 hover:bg-gray-100 hover:text-gray-800
                        border border-gray-200 rounded-lg transition-colors"
                    >
                      <Settings className="w-3.5 h-3.5" />
                      הגדרות חברה
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          {/* Add company */}
          <button
            onClick={() => setShowAddModal(true)}
            className="w-full flex items-center justify-center gap-2 py-3.5 border-2 border-dashed
              border-gray-200 rounded-2xl text-sm font-medium text-gray-400
              hover:border-primary-300 hover:text-primary-600 hover:bg-primary-50
              transition-all duration-200"
          >
            <Plus className="w-4 h-4" />
            הוסף חברה נוספת
          </button>
        </div>
      </div>

      <AddCompanyModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={handleCompanyCreated}
      />
    </>
  );
}
